import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { ThemeService } from '@bepatient/common';
import { getAccessibleTextColor } from '@bepatient/shared';
import { ModalController } from '@ionic/angular';
import { Browser } from '@capacitor/browser';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lib-webview-modal',
  templateUrl: './webview-modal.component.html',
  styleUrl: './webview-modal.component.css',
  standalone: false
})
export class WebviewModalComponent implements OnInit, OnDestroy, AfterViewInit {
  private modalController = inject(ModalController);
  private cdr = inject(ChangeDetectorRef);
  private themeService = inject(ThemeService);

  @Input() url: string = '';
  @Input() title: string = '';
  @Input() color: string = '';
  @Input() completionStatus: string = '';

  @ViewChild('webviewIframe', { static: false })
  iframeElement!: ElementRef<HTMLIFrameElement>;

  isLoading = true;
  contrastColor: string = '#ffffff';
  isCompleted = false;
  private themeSub?: Subscription;

  ngOnInit() {
    // Initialize completion status
    if (this.completionStatus) {
      this.isCompleted = this.completionStatus === 'completed';
    }

    this.applyContrast();
    // Re-derive the contrast text color on every theme / high-contrast toggle
    // so the modal title stays legible without a reload.
    this.themeSub = this.themeService.themeChange$.subscribe(() => {
      this.applyContrast();
      this.cdr.markForCheck();
    });

    // Set the dynamic color as CSS custom property
    if (this.color) {
      console.log('Setting custom color:', this.color);
      document.documentElement.style.setProperty('--modal-color', this.color);
    } else {
      console.log('Using fallback color');
      // Fallback to primary color if no color provided
      document.documentElement.style.setProperty(
        '--modal-color',
        'var(--ion-color-primary)'
      );
    }

    // Since BridgeWebviewComponent already handles initial loading
    if (this.url) {
      // Show loading, then hide after iframe should be ready
      setTimeout(() => {
        // Modal iframe ready, hiding loading
        this.isLoading = false;
        this.cdr.detectChanges();
      }, 1500);
    } else {
      // If no URL, hide immediately
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
  }

  /**
   * Derives the title contrast color from the study color via the canonical
   * WCAG-AA utility (docs/adr/0001-contrast-utility-consolidation.md). When no
   * concrete color is supplied the header falls back to the Ionic primary CSS
   * variable, which cannot be parsed, so we default to white text.
   */
  private applyContrast(): void {
    this.contrastColor = this.color
      ? getAccessibleTextColor(this.color)
      : '#ffffff';
  }

  async dismiss() {
    // WebviewModalComponent: dismiss() called
    try {
      await this.modalController.dismiss();
      // WebviewModalComponent: Modal dismissed successfully
    } catch (error) {
      console.error('WebviewModalComponent: Error dismissing modal:', error);
    }
  }

  async dismissWithCompletion() {
    console.log('WebviewModalComponent: dismissWithCompletion() called');
    this.updateCompletionStatus('completed');
    try {
      await this.modalController.dismiss({
        status: 'completed',
        completedAt: new Date().toISOString()
      });
      console.log(
        'WebviewModalComponent: Modal dismissed with completion status'
      );
    } catch (error) {
      console.error(
        'WebviewModalComponent: Error dismissing modal with completion:',
        error
      );
    }
  }

  /**
   * Update completion status and UI
   */
  updateCompletionStatus(status: string) {
    this.completionStatus = status;
    this.isCompleted = status === 'completed';
    this.cdr.detectChanges();
  }

  /**
   * Get status display text
   */
  getStatusDisplayText(): string {
    switch (this.completionStatus) {
      case 'completed':
        return 'Assessment Completed';
      case 'cancelled':
        return 'Assessment Cancelled';
      case 'error':
        return 'Assessment Error';
      case 'failed':
        return 'Assessment Failed';
      default:
        return 'Assessment Status: ' + this.completionStatus;
    }
  }

  /**
   * Get status color class
   */
  getStatusColorClass(): string {
    switch (this.completionStatus) {
      case 'completed':
        return 'status-completed';
      case 'cancelled':
        return 'status-cancelled';
      case 'error':
      case 'failed':
        return 'status-error';
      default:
        return 'status-default';
    }
  }

  onIframeLoad() {
    console.log('Webview iframe loaded successfully');
    this.isLoading = false;
    this.cdr.detectChanges();

    // Check if this is a completion URL
    this.checkForCompletion();
  }

  private checkForCompletion() {
    try {
      if (this.iframeElement?.nativeElement) {
        const iframe = this.iframeElement.nativeElement;
        const currentUrl = iframe.src;

        console.log('Checking iframe URL for completion:', currentUrl);

        // Check for assessment completion indicators
        if (this.isExternalAppCompletionUrl(currentUrl)) {
          console.log('Assessment completion detected in iframe URL');
          // Delay slightly to ensure the page has loaded
          setTimeout(() => {
            this.dismissWithCompletion();
          }, 1000);
        }
      }
    } catch (error) {
      console.log('Cannot access iframe URL due to CORS restrictions');
    }
  }

  private isExternalAppCompletionUrl(url: string): boolean {
    // Check for common assessment completion indicators
    const completionIndicators = [
      'completed',
      'finished',
      'success',
      'done',
      'thank-you',
      'assessment-complete'
    ];

    return completionIndicators.some(indicator =>
      url.toLowerCase().includes(indicator)
    );
  }

  // Check if we should show the completion button (only in development)
  shouldShowCompletionButton(): boolean {
    return window.location.hostname === 'localhost';
  }

  onIframeError() {
    console.error('Webview iframe failed to load');
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  onIframeLoadStart() {
    console.log('Webview iframe load started');
    this.cdr.detectChanges();
  }

  onIframeDOMContentLoaded() {
    console.log('Webview iframe DOM content loaded');
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    // Final fallback: hide loading after 2 seconds
    setTimeout(() => {
      if (this.isLoading) {
        console.log('Final fallback: Force hiding loading after 2 seconds');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    }, 2000);
  }

  debugIframeStatus() {
    if (this.iframeElement?.nativeElement) {
      const iframe = this.iframeElement.nativeElement;
      try {
        console.log('Iframe Debug Info:', {
          src: iframe.src,
          readyState: iframe.contentWindow?.document?.readyState,
          contentLoaded: iframe.contentWindow?.document?.body?.innerHTML?.length
            ? iframe.contentWindow.document.body.innerHTML.length > 0
            : false
        });
      } catch (error) {
        console.log('Iframe Debug Info (CORS blocked):', {
          src: iframe.src,
          corsError:
            'Cannot access iframe content due to cross-origin restrictions',
          note: 'This is normal for external websites'
        });
      }
    } else {
      console.log('Iframe element not available for debugging');
    }
  }

  async openInBrowser(): Promise<void> {
    console.log('Opening URL in external browser');
    try {
      await Browser.open({
        url: this.url,
        presentationStyle: 'fullscreen'
      });
      this.dismiss();
    } catch (error) {
      console.error('Failed to open browser:', error);
    }
  }
}
